import {
  Component,
  computed,
  effect,
  HostBinding,
  input,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AbstractSuiControl, SuiIconComponent } from '@stride-ui/components';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';

type CounterDirection = -1 | 1;

@Component({
  selector: 'tnq-counter-row',
  imports: [SuiIconComponent, TranslateModule],
  templateUrl: './counter-row.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './counter-row.component.scss',
})
export class CounterRowComponent
  extends AbstractSuiControl<number>
  implements OnInit
{
  @HostBinding('class.tnq-counter-row') readonly hostClass = true;

  readonly label = input.required<string>();
  readonly icon = input<string>();
  readonly editable = input<boolean>(false);
  readonly min = input<number>(0);
  readonly max = input<number>(99);
  readonly step = input<number>(1);
  readonly count = input<number | null>(null);
  readonly decreaseAriaLabel = input<string>('global.decrease');
  readonly increaseAriaLabel = input<string>('global.increase');

  readonly currentValue = signal<number>(0);
  readonly disabledState = signal<boolean>(false);

  private readonly canAdjust = computed(
    () => this.editable() && !this.disabledState(),
  );
  readonly canDecrease = computed(
    () => this.canAdjust() && this.currentValue() > this.min(),
  );
  readonly canIncrease = computed(
    () => this.canAdjust() && this.currentValue() < this.max(),
  );

  constructor() {
    super();
    effect(() => {
      const explicit = this.count();
      if (explicit != null) {
        this.currentValue.set(this.clamp(explicit));
      }
    });
  }

  ngOnInit(): void {
    this.valueUpdated$
      .pipe(takeUntil(this.destroyNotifier$))
      .subscribe((next) =>
        this.currentValue.set(this.clamp(next ?? this.min())),
      );
  }

  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
    this.disabledState.set(isDisabled);
  }

  decrease(): void {
    this.adjust(-1);
  }

  increase(): void {
    this.adjust(1);
  }

  onKeydown(event: KeyboardEvent): void {
    if (!this.canAdjust()) {
      return;
    }
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        this.increase();
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        this.decrease();
        break;
      case 'Home':
        this.commit(this.min());
        break;
      case 'End':
        this.commit(this.max());
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  protected getFallbackValue(): number {
    return 0;
  }

  private adjust(direction: CounterDirection): void {
    const allowed = direction === -1 ? this.canDecrease() : this.canIncrease();
    if (!allowed) {
      return;
    }
    this.commit(this.currentValue() + direction * this.step());
  }

  private commit(next: number): void {
    const clamped = this.clamp(next);
    this.currentValue.set(clamped);
    this.value = clamped;
    this.changed.emit(clamped);
    this.onTouched();
  }

  private clamp(value: number): number {
    if (!Number.isFinite(value)) {
      return this.min();
    }
    return Math.min(Math.max(value, this.min()), this.max());
  }
}
